import React, { useState } from 'react';
import { observer } from 'mobx-react';

import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Popover, Divider } from '@mui/material';

import { useStore } from 'utils/hooks/store';
import { FolderType } from 'utils/api/modules/entities/entities.types';

import DeleteFolderModal from '../Modals/DeleteFolderModal';
import EditFolderModal from '../Modals/EditFolderModal';
import FolderFormModal from '../Modals/FolderFormModal';
import { MenuButton, MenuItem, ModalBoundary } from './styles';

const FolderMenu: React.FC<{
  folder: FolderType;
}> = ({ folder }) => {
  const { folders } = useStore();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateSubfolderModal, setShowCreateSubfolderModal] = useState(false);

  const handleMenuClick = (e: React.MouseEvent<HTMLElement>): void => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  const handleClose = (): void => {
    setAnchorEl(null);
  };

  const handleCreateSubfolder = (): void => {
    handleClose();
    setShowCreateSubfolderModal(true);
  };

  const handleCreateSubfolderSubmit = async (name: string, parent: string | null): Promise<void> => {
    await folders.createFolder(name, parent);
    setShowCreateSubfolderModal(false);
  };

  const handleEditFolder = (): void => {
    handleClose();
    setShowEditModal(true);
  };

  const handleSaveEdit = async (name: string, parent: string | null): Promise<void> => {
    await folders.editFolder(folder.uuid, name, parent);
    setShowEditModal(false);
  };

  const handleDeleteFolder = (): void => {
    handleClose();
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async (): Promise<void> => {
    await folders.deleteFolder(folder.uuid);
    setShowDeleteModal(false);
  };

  return (
    <>
      <MenuButton className="menu-button" size="small" onClick={handleMenuClick}>
        <MoreVertIcon fontSize="small" />
      </MenuButton>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          onClick={(e) => {
            e.stopPropagation();
            handleCreateSubfolder();
          }}
        >
          Create subfolder
        </MenuItem>
        <MenuItem
          onClick={(e) => {
            e.stopPropagation();
            handleEditFolder();
          }}
        >
          Edit folder
        </MenuItem>
        <Divider sx={{ m: 0 }} />
        <MenuItem
          onClick={(e) => {
            e.stopPropagation();
            handleDeleteFolder();
          }}
          sx={{ color: '#D32F2F' }}
        >
          Delete folder
        </MenuItem>
      </Popover>

      {/* Portal clicks bubble through React to the row's expand toggle. */}
      <ModalBoundary onClick={(e) => e.stopPropagation()}>
        {showDeleteModal && (
          <DeleteFolderModal
            folder={folder}
            open={showDeleteModal}
            onClose={() => setShowDeleteModal(false)}
            onDelete={handleConfirmDelete}
          />
        )}

        {showEditModal && (
          <EditFolderModal
            open={showEditModal}
            folder={folder}
            folders={folders.folders}
            onClose={() => setShowEditModal(false)}
            onSave={handleSaveEdit}
          />
        )}

        {showCreateSubfolderModal && (
          <FolderFormModal
            open={showCreateSubfolderModal}
            title="Create New Folder"
            submitButtonText="Create folder"
            initialParent={folder.uuid}
            folders={folders.folders}
            onClose={() => setShowCreateSubfolderModal(false)}
            onSubmit={handleCreateSubfolderSubmit}
          />
        )}
      </ModalBoundary>
    </>
  );
};

export default observer(FolderMenu);
